
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Bell, Calendar, Clock, Mail, MessageSquare, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';

interface DashboardHeaderProps {
  title: string;
  description?: string;
  className?: string;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  title,
  description,
  className,
}) => {
  const navigate = useNavigate();
  const currentDate = new Date();
  const formattedDate = format(currentDate, 'EEEE, MMMM d, yyyy');
  const formattedTime = format(currentDate, 'h:mm a');

  const handleNotificationClick = (route: string) => {
    navigate(route);
  };

  return (
    <div className={cn("flex flex-col space-y-2 md:space-y-0 md:flex-row justify-between items-start md:items-center py-4", className)}>
      <div className="flex flex-col">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      
      <div className="flex items-center space-x-4">
        <div className="hidden sm:flex items-center text-sm text-muted-foreground">
          <Calendar className="w-4 h-4 mr-1.5" />
          <span>{formattedDate}</span>
          <div className="mx-2 h-1 w-1 rounded-full bg-muted-foreground/30" />
          <Clock className="w-4 h-4 mr-1.5" />
          <span>{formattedTime}</span>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="relative h-9 w-9 rounded-full"
            >
              <Bell className="h-4 w-4" />
              <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                3
              </span>
              <span className="sr-only">Notifications</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[320px]">
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <h3 className="font-medium text-sm">New Features Available</h3>
            </div>

            <DropdownMenuItem
              className="flex flex-col items-start p-3 cursor-pointer hover:bg-accent"
              onClick={() => handleNotificationClick('/sms-campaigns')}
            >
              <div className="flex items-center gap-2 w-full">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">SMS Campaigns & Triggers</span>
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Create automated SMS campaigns and set up smart triggers for customer engagement
                  </p>
                </div>
              </div>
            </DropdownMenuItem>

            <DropdownMenuItem
              className="flex flex-col items-start p-3 cursor-pointer hover:bg-accent"
              onClick={() => handleNotificationClick('/email-campaigns')}
            >
              <div className="flex items-center gap-2 w-full">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">Email Campaigns & Triggers</span>
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Design beautiful email campaigns and automate customer communications
                  </p>
                </div>
              </div>
            </DropdownMenuItem>

            <DropdownMenuItem
              className="flex flex-col items-start p-3 cursor-pointer hover:bg-accent"
              onClick={() => handleNotificationClick('/media')}
            >
              <div className="flex items-center gap-2 w-full">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Image className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">Media Library</span>
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Upload and manage images for your campaigns and marketing materials
                  </p>
                </div>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default DashboardHeader;
